/// <reference path="../../../../../typings/index.d.ts" />

module Meevo.Components.Controls {
    "use strict";

    // Shared editor for product lot allocations (rows of lot typeahead / md-select + qty + add/remove).
    // Presentation + light orchestration only; all business rules (required, add eligibility, historical
    // options, mismatch wording, disabled/read-only states) are passed in by the host. See
    // transactionEditor, register productUsage, register sale/historical-refund and manual-refund lot screen.
    class MeevoLotAllocationEditor implements angular.IComponentController {
        allocations: any[];
        targetQuantity: number;
        variantId: string;      // used to build the lot typeahead criteria
        inventoryType: number;  // "
        locationId: number;
        required: boolean;
        readonly: boolean;
        readonlyRows: any[];
        disabled: boolean;
        alwaysShowQuantity: boolean; // true => always show qty; default false => only when target qty > 1
        canAddLot: boolean;     // optional override; when unset the component computes "under-allocated"
        historicalOptions: (arg: { lot: any }) => any[];
        mismatchMessage: string;
        contentKey: string;
        helpText: string;
        rowInvalid: (arg: { lot: any }) => boolean;
        onLotChanged: (arg: { lot: any }) => void;
        onQtyChanged: (arg: { lot: any }) => void;

        private get lotUtil() { return Meevo.Components.Common.LotAllocationUtil; }
        private cachedCriteria: any;

        $onInit() {
            if (!this.contentKey) { this.contentKey = "LABEL_LOT_NUMBER"; }
        }

        // Build the lot typeahead criteria once and cache it, rebuilding only when the variant/inventory
        // type changes — a fresh object every digest would churn the typeahead's query.
        getCriteria(): any {
            if (!this.cachedCriteria
                || this.cachedCriteria.variant_TId !== this.variantId
                || this.cachedCriteria.productInventoryType !== this.inventoryType) {
                this.cachedCriteria = this.lotUtil.buildCriteria(this.variantId, this.inventoryType);
            }
            return this.cachedCriteria;
        }

        shouldShowQty(): boolean {
            if (this.readonly) { return false; }
            return !!this.alwaysShowQuantity || Math.abs(this.targetQuantity || 0) > 1;
        }

        // Default: show "add another lot" only while under-allocated. A host with its own rule
        // (register's historical-lot cap, manual refund's always-on) passes can-add-lot to override.
        canAdd(): boolean {
            if (this.readonly || this.disabled) { return false; }
            if (this.canAddLot != null) { return !!this.canAddLot; }
            const target = Math.abs(this.targetQuantity || 0);
            return target > 1 && this.lotUtil.qtyTotal(this.allocations) < target;
        }

        // Historical-lot rows render an md-select (pick from prior lots); everything else uses the
        // typeahead. Driven purely by whether the host supplied historical options for this row.
        hasHistoricalOptions(lot: any): boolean {
            return (this.historicalOptions({ lot: lot }) || []).length > 0;
        }

        // Historical-refund md-select requires a lot to be chosen before its qty can be entered.
        isQtyDisabled(lot: any): boolean {
            return !!this.disabled || (this.hasHistoricalOptions(lot) && !lot.variantLotId);
        }

        handleLotChanged(lot: any): void {
            if (this.hasHistoricalOptions(lot)) {
                // md-select binds the id directly; the host re-derives the object (onHistoricalLotSelected).
                this.onLotChanged({ lot: lot });
            } else {
                // typeahead binds the object; keep the primitive id in sync. No host callback needed.
                this.lotUtil.syncSelectedId(lot);
            }
        }

        handleQtyChanged(lot: any): void {
            this.onQtyChanged({ lot: lot });
        }

        addLot(): void {
            if (!this.allocations) { this.allocations = []; }
            this.lotUtil.addRow(this.allocations);
        }

        removeLot(index: number): void {
            this.lotUtil.removeRow(this.allocations, index);
        }

        isRowInvalid(lot: any): boolean {
            return !!this.rowInvalid({ lot: lot });
        }
    }

    angular.module('meevo.components.controls')
        .component('meevoLotAllocationEditor', <angular.IComponentOptions>{
            templateUrl: 'app/components/controls/lotAllocationEditor/lotAllocationEditor.component.html',
            controller: MeevoLotAllocationEditor,
            controllerAs: '$ctrl',
            bindings: {
                allocations: '<',
                targetQuantity: '<',
                variantId: '<',
                inventoryType: '<',
                locationId: '<',
                required: '<',
                readonly: '<',
                readonlyRows: '<',
                disabled: '<',
                alwaysShowQuantity: '<',
                canAddLot: '<',
                historicalOptions: '&',
                mismatchMessage: '<',
                contentKey: '@',
                helpText: '@',
                rowInvalid: '&',
                onLotChanged: '&',
                onQtyChanged: '&'
            }
        });
}
